/** Debug overlay showing screen-edge indicators for enemies outside the camera viewport. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { magnitude } from '../../utils/Math';
import { crispText, crispRect } from '../../ui/CrispUtil';
import { TextStyle } from '../../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT, SF0_X, SF0_Y } from '../../utils/Constants';

const INDICATOR_MARGIN = 12;
const INDICATOR_SCALE = 0.5;
const INDICATOR_ALPHA = 0.5;

/** Pooled screen-edge indicators for enemies outside the camera viewport. Reuses objects to avoid GPU texture churn. */
export class OffscreenOverlay {
  private scene: Phaser.Scene;
  private rectPool: Phaser.GameObjects.Rectangle[] = [];
  private textPool: Phaser.GameObjects.Text[] = [];
  private activeCount = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Update pooled screen-edge indicators for all enemies currently outside the camera view. */
  draw(_graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    const cam = this.scene.cameras.main;
    const camLeft = cam.worldView.x;
    const camRight = camLeft + cam.worldView.width;
    const camTop = cam.worldView.y;
    const camBottom = camTop + cam.worldView.height;
    const screenW = GAME_WIDTH;
    const screenH = GAME_HEIGHT;
    const centerX = camLeft + cam.worldView.width / 2;
    const centerY = camTop + cam.worldView.height / 2;

    let idx = 0;

    for (const enemy of enemies) {
      const ex = enemy.sprite.x;
      const ey = enemy.sprite.y;

      if (ex >= camLeft && ex <= camRight && ey >= camTop && ey <= camBottom) continue;

      const dx = ex - centerX;
      const dy = ey - centerY;
      const dist = magnitude(dx, dy);

      const halfW = screenW / 2 - INDICATOR_MARGIN;
      const halfH = screenH / 2 - INDICATOR_MARGIN;

      let screenX: number;
      let screenY: number;

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx / halfW > absDy / halfH) {
        const scale = halfW / absDx;
        screenX = SF0_X + screenW / 2 + dx * scale;
        screenY = SF0_Y + screenH / 2 + dy * scale;
      } else {
        const scale = halfH / absDy;
        screenX = SF0_X + screenW / 2 + dx * scale;
        screenY = SF0_Y + screenH / 2 + dy * scale;
      }

      const archColor = ConfigLoader.getArchetypeColor(enemy.config.id);
      const baseSize = enemy.config.weight === 'heavy' || enemy.config.weight === 'immovable' ? 20 : 14;
      const size = baseSize * INDICATOR_SCALE;

      const indicator = this.getOrCreateRect(idx);
      indicator.setPosition(screenX, screenY);
      indicator.setFillStyle(archColor);
      indicator.setVisible(true);

      const distLabel = this.getOrCreateText(idx);
      const distStr = `${Math.round(dist)}`;
      if (distLabel.text !== distStr) distLabel.setText(distStr);
      distLabel.setPosition(screenX, screenY + size / 2 + 2);
      distLabel.setVisible(true);

      idx++;
    }

    for (let i = idx; i < this.activeCount; i++) {
      this.rectPool[i].setVisible(false);
      this.textPool[i].setVisible(false);
    }
    this.activeCount = idx;
  }

  private getOrCreateRect(index: number): Phaser.GameObjects.Rectangle {
    if (index < this.rectPool.length) return this.rectPool[index];
    const rect = crispRect(this.scene, 0, 0, 10, 10, 0xffffff)
      .setAlpha(INDICATOR_ALPHA).setScrollFactor(0).setDepth(2002);
    this.rectPool.push(rect);
    return rect;
  }

  private getOrCreateText(index: number): Phaser.GameObjects.Text {
    if (index < this.textPool.length) return this.textPool[index];
    const text = crispText(this.scene, 0, 0, '', { ...TextStyle.debugWorld, color: '#ffffff' })
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(2002);
    this.textPool.push(text);
    return text;
  }

  /** Hide all active indicators. */
  clear(): void {
    for (let i = 0; i < this.activeCount; i++) {
      this.rectPool[i].setVisible(false);
      this.textPool[i].setVisible(false);
    }
    this.activeCount = 0;
  }

  /** Destroy all pooled objects and free resources. */
  destroy(): void {
    for (const r of this.rectPool) r.destroy();
    this.rectPool = [];
    for (const t of this.textPool) t.destroy();
    this.textPool = [];
    this.activeCount = 0;
  }
}
